/**
 * Where a ticket has got to, as four stages rather than a status word.
 *
 * "Waiting for Resolution" tells someone who works in IT exactly where their
 * ticket is. It tells a machine operator nothing — not whether anyone has
 * looked at it, not whether it is nearly done, not whether the next move is
 * theirs. Ivanti's own self-service portal draws a four-stop track for this
 * reason, and it is the one thing on that page worth copying outright.
 *
 * Eleven provider statuses collapse onto four stages. That loses detail on
 * purpose: the detail is still in the status chip above, and someone who wants
 * it can read it there.
 *
 * `CANCELLED` deliberately has no stage. A cancelled ticket did not travel this
 * path, and drawing it at "Closed" with three green stops behind it would say
 * it completed. It renders nothing and the status chip carries the meaning.
 */

import { Timeline, type TimelineItem } from "@norican/react";

import { useT } from "@/i18n";

import type { TicketStatusCode } from "../api/contract";

/** The four stops, in order. Index doubles as progress. */
const STAGES = ["logged", "inProgress", "resolved", "closed"] as const;

/**
 * Which stop a provider status sits at.
 *
 * `PENDING_APPROVAL` and `APPROVED` map to "In progress": from the employee's
 * side an approval is somebody else working on it, which is what that stop
 * means. `UNKNOWN` falls back to "Logged" — a ticket exists, so at minimum it
 * has been logged, and claiming more would be a guess.
 */
const STAGE_BY_STATUS: Partial<Record<TicketStatusCode, number>> = {
  LOGGED: 0,
  UNKNOWN: 0,
  ACTIVE: 1,
  WAITING_CUSTOMER: 1,
  WAITING_THIRD_PARTY: 1,
  WAITING_RESOLUTION: 1,
  PENDING_APPROVAL: 1,
  APPROVED: 1,
  // Still somebody else working on it, from where the requester sits.
  IN_PROCUREMENT: 1,
  RESOLVED: 2,
  FULFILLED: 2,
  CLOSED: 3,
};

export interface TicketProgressProps {
  status: TicketStatusCode;
  className?: string;
}

export function TicketProgress({ status, className }: TicketProgressProps) {
  const { t } = useT("tickets");
  const active = STAGE_BY_STATUS[status];

  if (active === undefined) {
    return null;
  }

  const items: TimelineItem[] = STAGES.map((stage, index) => ({
    id: stage,
    title: t(`progress.${stage}`),
    status: index < active ? "complete" : index === active ? "current" : "upcoming",
  }));

  return (
    <Timeline
      orientation="horizontal"
      items={items}
      className={className}
      aria-label={t("progress.label")}
    />
  );
}
